import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  TextField,
  IconButton,
  InputAdornment,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { UserContext } from "../context/userContext";
import { ResetMyPassword, SendPasswordResetCode } from "../configuration/AAConfig_Security";
import { FormRowStyle, FormInputStyle } from "../utils/AAStyles";

export default function PostLoginResetDialog({ onClose }) {
  const {
    currentUser,
    updateResetCode,
    resetCode,
    updateShowAuthenticate,
    showResetPasswordDialog,
  } = useContext(UserContext);

  const [step, setStep] = useState(1);
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState("");
  const [disabled, setDisabled] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [shouldTriggerSendCode, setShouldTriggerSendCode] = useState(false);
  const hasSentCode = useRef(false);

  useEffect(() => {
    if (showResetPasswordDialog) {
      setShouldTriggerSendCode(true);
    }
  }, [showResetPasswordDialog]);

  useEffect(() => {
    const sendCode = async () => {
      if (!showResetPasswordDialog || !currentUser || hasSentCode.current || !shouldTriggerSendCode) return;
      hasSentCode.current = true;
      setShouldTriggerSendCode(false);

      const auditName = currentUser?.auditName;
      if (!auditName) {
        setError("Missing audit name for user.");
        return;
      }

      try {
        const authorizeId = await SendPasswordResetCode(auditName, (err) => setError(err));
        if (!authorizeId) return;

        updateResetCode(authorizeId);
        updateShowAuthenticate(false);
        setStep(2);
      } catch (err) {
        console.error("Send code error:", err);
        setError("Failed to send reset code.");
      }
    };

    if (showResetPasswordDialog && shouldTriggerSendCode) {
      hasSentCode.current = false;
      setStep(1);
      setCode("");
      setPassword("");
      setConfirmPassword("");
      setError("");
      setDisabled(false);
      setResetSuccess(false);
      sendCode();
    }
  }, [showResetPasswordDialog, currentUser, updateResetCode, updateShowAuthenticate, shouldTriggerSendCode]);

  const handleReset = async () => {
    if (!code || !password || !confirmPassword) {
      setError("All fields are required.");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setDisabled(true);
    setError("");
    setResetSuccess(false);

    let apiError = "";
    await ResetMyPassword(
      {
        resetId: resetCode,
        code,
        password,
        confirmPassword,
      },
      (err) => {
        apiError = err;
      }
    );

    setDisabled(false);

    if (apiError) {
      setError(apiError);
      setResetSuccess(false);
    } else {
      setResetSuccess(true);
    }
  };

  return (
    <Dialog open={showResetPasswordDialog} onClose={onClose} fullWidth maxWidth="xs" disableEnforceFocus>
      <DialogTitle sx={{ backgroundColor: "#2A6EBB", color: "#fff" }}>
        {step === 1 ? "Sending Reset Code..." : "Set New Password"}
      </DialogTitle>
      <DialogContent>
        {step === 2 && (
          <Grid container justifyContent="flex-start" alignItems="center" sx={FormRowStyle()}>
            <Grid item xs={12} sx={{ mb: 1 }}>
              <Typography variant="body2" color="textSecondary">
                Please enter the reset code sent to your email and set a new password.
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="body2" align="left">Reset Code*</Typography>
            </Grid>
            <Grid item xs={9}>
              <TextField
                sx={FormInputStyle()}
                fullWidth
                required
                margin="dense"
                size="small"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                disabled={disabled}
              />
            </Grid>
            <Grid item xs={3}>
              <Typography variant="body2" align="left">Password*</Typography>
            </Grid>
            <Grid item xs={9}>
              <TextField
                sx={FormInputStyle()}
                type={showPassword ? "text" : "password"}
                fullWidth
                required
                margin="dense"
                size="small"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={disabled}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        size="small"
                      >
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={3}>
              <Typography variant="body2" align="left">Confirm*</Typography>
            </Grid>
            <Grid item xs={9}>
              <TextField
                sx={FormInputStyle()}
                type={showConfirmPassword ? "text" : "password"}
                fullWidth
                required
                margin="dense"
                size="small"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={disabled}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        edge="end"
                        size="small"
                      >
                        {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            {error && (
              <Grid item xs={12}>
                <Typography color="error">{error}</Typography>
              </Grid>
            )}
            {!error && resetSuccess && (
              <Grid item xs={12}>
                <Typography color="green">Password updated successfully. You may now close this window.</Typography>
              </Grid>
            )}
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        {step === 2 && (
          <Button
            onClick={handleReset}
            variant="contained"
            color="primary"
            disabled={disabled}
          >
            Reset Password
          </Button>
        )}
        <Button onClick={onClose} disabled={disabled}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
}
